import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';

import { InbetriebnahmeService } from '../inbetriebnahme.service';
import { OfflineDbService } from './offline-db.service';
import { OfflineSyncService } from './offline-sync.service';

import {
  OFFLINE_SCHEMA_VERSION,
  OfflineListeEintrag,
  OfflineListeStatus,
  OfflinePaket
} from '../../Models/offline.model';

/** Was beim Aktualisieren bzw. Entfernen herausgekommen ist. */
export type OfflineAktionErgebnis =
  | { ok: true; standUtc: string }
  | { ok: false; grund: 'ausstehend' | 'konflikt' | 'fehler'; meldung: string; anzahl?: number };

/**
 * Lädt eine Inbetriebnahme für die Offline-Nutzung herunter, hält sie aktuell
 * und entfernt sie wieder.
 *
 * Drei Regeln, die den Unterschied machen:
 *
 * 1. Als „offline verfügbar" gilt eine Inbetriebnahme erst, wenn ALLES
 *    gespeichert ist. Der Kopfsatz entsteht in derselben Transaktion wie die
 *    Messstellen – ein abgebrochener Download hinterlässt keinen halben
 *    Bestand, mit dem der Monteur vor Ort auf die Nase fällt.
 *
 * 2. Ein Download überschreibt NIE nicht synchronisierte Änderungen. Vor dem
 *    Aktualisieren geht erst alles Ausstehende zum Server; bleibt etwas
 *    liegen, wird nicht heruntergeladen.
 *
 * 3. Entfernt wird nur, was vollständig angekommen ist.
 */
@Injectable({ providedIn: 'root' })
export class OfflineDownloadService {

  /**
   * Zustand je Inbetriebnahme, für die Übersicht. Als Strom, damit mehrere
   * Ansichten dasselbe zeigen, ohne sich gegenseitig abzufragen.
   */
  private readonly status$ = new BehaviorSubject<OfflineListeStatus[]>([]);

  /** Welche Liste gerade heruntergeladen wird (für den Spinner am Button). */
  private readonly laufend = new Set<number>();

  constructor(
    private api: InbetriebnahmeService,
    private db: OfflineDbService,
    private sync: OfflineSyncService
  ) {
    void this.aktualisiereStatus();
  }

  get offlineStatus$(): Observable<OfflineListeStatus[]> {
    return this.status$.asObservable();
  }

  laeuftGerade(listeId: number): boolean {
    return this.laufend.has(listeId);
  }

  /** Zustand einer einzelnen Inbetriebnahme aus dem zuletzt ermittelten Stand. */
  statusVon(listeId: number): OfflineListeStatus | null {
    return this.status$.value.find(s => s.listeId === listeId) ?? null;
  }

  // ===================================================================
  // OFFLINE VERFÜGBAR MACHEN
  // ===================================================================

  /**
   * Lädt das komplette Paket und schreibt es lokal.
   *
   * Braucht zwingend eine Verbindung – offline lässt sich nichts
   * herunterladen, und das ist genau der Grund, warum es vorher gemacht
   * werden muss.
   */
  async machOfflineVerfuegbar(listeId: number): Promise<OfflineAktionErgebnis> {
    if (!(await this.db.istVerfuegbar())) {
      return {
        ok: false,
        grund: 'fehler',
        meldung:
          'Dieser Browser stellt keinen lokalen Speicher bereit. '
          + 'Im privaten Modus ist die Offline-Nutzung nicht möglich.'
      };
    }

    this.laufend.add(listeId);

    try {
      const paket = await firstValueFrom(this.api.getOfflinePaket(listeId));

      if (!paket) {
        return { ok: false, grund: 'fehler', meldung: 'Der Server hat keine Daten geliefert.' };
      }

      await this.speicherePaket(paket);
      await this.aktualisiereStatus();

      return { ok: true, standUtc: paket.erzeugtAmUtc };
    } catch (fehler: any) {
      return {
        ok: false,
        grund: 'fehler',
        meldung: this.fehlertext(fehler)
      };
    } finally {
      this.laufend.delete(listeId);
    }
  }

  // ===================================================================
  // OFFLINE-DATEN AKTUALISIEREN
  // ===================================================================

  /**
   * Holt den aktuellen Serverstand – aber erst, nachdem alles Erfasste
   * angekommen ist.
   *
   * Die Reihenfolge ist zwingend: erst synchronisieren, dann auf Konflikte
   * prüfen, erst danach herunterladen. Andersherum würde der Download die
   * eigenen, noch nicht übertragenen Prüfungen überschreiben – ein ganzer
   * Arbeitstag wäre weg, ohne dass irgendwo ein Fehler aufträte.
   */
  async aktualisiere(listeId: number): Promise<OfflineAktionErgebnis> {
    this.laufend.add(listeId);

    try {
      // 1. Ausstehendes zuerst hochschicken.
      await this.sync.synchronisiere('manuell');

      // 2. Was bleibt liegen?
      const konflikte = await this.db.zaehleKonflikte(listeId);

      if (konflikte > 0) {
        return {
          ok: false,
          grund: 'konflikt',
          anzahl: konflikte,
          meldung:
            konflikte === 1
              ? 'Ein Synchronisierungskonflikt ist offen. Bitte zuerst entscheiden, welche Fassung gelten soll.'
              : `${konflikte} Synchronisierungskonflikte sind offen. Bitte zuerst entscheiden, welche Fassung gelten soll.`
        };
      }

      const ausstehend = await this.db.zaehleAusstehend(listeId);

      if (ausstehend > 0) {
        return {
          ok: false,
          grund: 'ausstehend',
          anzahl: ausstehend,
          meldung:
            ausstehend === 1
              ? 'Eine Änderung wurde noch nicht synchronisiert. Die Offline-Daten werden erst danach aktualisiert.'
              : `${ausstehend} Änderungen wurden noch nicht synchronisiert. Die Offline-Daten werden erst danach aktualisiert.`
        };
      }

      // 3. Jetzt ist der Weg frei.
      const paket = await firstValueFrom(this.api.getOfflinePaket(listeId));

      await this.speicherePaket(paket);
      await this.aktualisiereStatus();

      return { ok: true, standUtc: paket.erzeugtAmUtc };
    } catch (fehler: any) {
      return { ok: false, grund: 'fehler', meldung: this.fehlertext(fehler) };
    } finally {
      this.laufend.delete(listeId);
    }
  }

  // ===================================================================
  // OFFLINE-DATEN ENTFERNEN
  // ===================================================================

  /**
   * Entfernt den lokalen Bestand – aber nur, wenn nichts mehr aussteht.
   *
   * Das ist keine Bequemlichkeit, sondern Datenschutz im wörtlichen Sinn: die
   * Warteschlange ist der EINZIGE Ort, an dem eine offline erfasste Prüfung
   * existiert, solange sie nicht beim Server ist. Sie zu löschen hieße, die
   * Arbeit zu löschen.
   */
  async entferne(listeId: number): Promise<OfflineAktionErgebnis> {
    const ausstehend = await this.db.zaehleAusstehend(listeId);
    const konflikte = await this.db.zaehleKonflikte(listeId);
    const offen = ausstehend + konflikte;

    if (offen > 0) {
      return {
        ok: false,
        grund: konflikte > 0 ? 'konflikt' : 'ausstehend',
        anzahl: offen,
        meldung:
          offen === 1
            ? 'Eine Änderung wurde noch nicht synchronisiert. Die Offline-Daten können deshalb nicht entfernt werden.'
            : `${offen} Änderungen wurden noch nicht synchronisiert. Die Offline-Daten können deshalb nicht entfernt werden.`
      };
    }

    const db = this.db.db;

    await db.transaction('rw', db.listen, db.messstellen, db.details, async () => {
      await db.messstellen.where('messstellenListeId').equals(listeId).delete();
      await db.details.where('messstellenListeId').equals(listeId).delete();
      await db.listen.delete(listeId);
    });

    await this.aktualisiereStatus();

    return { ok: true, standUtc: new Date().toISOString() };
  }

  // ===================================================================
  // SPEICHERN
  // ===================================================================

  /**
   * Schreibt das Paket lokal – vollständig oder gar nicht.
   *
   * Alles läuft in EINER Transaktion. Bricht der Vorgang mittendrin ab (der
   * Browser wird geschlossen, der Speicher ist voll), bleibt der bisherige
   * Zustand stehen. Ein halb geschriebener Bestand wäre schlimmer als gar
   * keiner: er sähe vollständig aus.
   */
  private async speicherePaket(paket: OfflinePaket): Promise<void> {
    if (paket.schemaVersion !== OFFLINE_SCHEMA_VERSION) {
      /*
       * Server und Anwendung sind verschieden alt. Weiterzumachen hieße, ein
       * unbekanntes Format zu speichern – lieber ein klarer Hinweis.
       */
      throw new Error(
        'Die Anwendung ist nicht auf demselben Stand wie der Server. '
        + 'Bitte die Seite neu laden und es erneut versuchen.'
      );
    }

    const listeId = paket.liste.id;
    const db = this.db.db;

    const kopf: OfflineListeEintrag = {
      id: listeId,
      name: paket.liste.name,
      projektId: paket.liste.projektId ?? null,
      projektName: paket.liste.projektName ?? null,
      projektNummer: paket.liste.projektNummer ?? null,
      customerId: paket.liste.customerId ?? null,
      customerName: paket.liste.customerName ?? null,
      downloadedAtUtc: paket.erzeugtAmUtc,
      lastSyncAtUtc: await this.bisherigerSyncStand(listeId),
      schemaVersion: paket.schemaVersion,
      canEdit: paket.canEdit,
      liste: paket.liste,
      columnClassifications: paket.columnClassifications ?? null,
      messstellenCount: paket.messstellen.length
    };

    await db.transaction(
      'rw',
      db.listen,
      db.messstellen,
      db.details,
      db.kategorien,
      async () => {
        // Alten Bestand dieser Liste wegräumen. Messstellen, die es am Server
        // nicht mehr gibt, dürfen lokal nicht als Karteileichen zurückbleiben.
        await db.messstellen.where('messstellenListeId').equals(listeId).delete();
        await db.details.where('messstellenListeId').equals(listeId).delete();

        await db.messstellen.bulkPut(
          paket.messstellen.map(m => ({
            id: m.id,
            messstellenListeId: listeId,
            rowNumber: m.rowNumber,
            data: m
          }))
        );

        await db.details.bulkPut(
          paket.details.map(d => ({
            id: d.id,
            messstellenListeId: listeId,
            data: d
          }))
        );

        // Kategorien gelten anwendungsweit, nicht je Liste – deshalb einfach
        // überschreiben statt vorher zu löschen. Ein anderer Bestand könnte
        // sie sonst mitverlieren.
        await db.kategorien.bulkPut(
          paket.kategorien.map(k => ({ id: k.id, data: k }))
        );

        // Zuletzt: erst jetzt gilt die Inbetriebnahme als offline verfügbar.
        await db.listen.put(kopf);
      }
    );
  }

  private async bisherigerSyncStand(listeId: number): Promise<string | null> {
    const vorhanden = await this.db.ladeListe(listeId);

    return vorhanden?.lastSyncAtUtc ?? null;
  }

  // ===================================================================
  // STATUS
  // ===================================================================

  /** Ermittelt den Offline-Zustand aller lokal vorhandenen Inbetriebnahmen neu. */
  async aktualisiereStatus(): Promise<OfflineListeStatus[]> {
    if (!(await this.db.istVerfuegbar())) {
      this.status$.next([]);
      return [];
    }

    const listen = await this.db.ladeListen();
    const warteschlange = await this.db.ladeWarteschlange();
    const konflikte = await this.db.ladeKonflikte();

    const status = listen.map(l => ({
      listeId: l.id,
      verfuegbar: true,
      downloadedAtUtc: l.downloadedAtUtc,
      lastSyncAtUtc: l.lastSyncAtUtc ?? null,
      ausstehend: warteschlange.filter(e => e.messstellenListeId === l.id).length,
      konflikte: konflikte.filter(k => k.messstellenListeId === l.id).length,
      schemaVeraltet: l.schemaVersion !== OFFLINE_SCHEMA_VERSION
    }));

    this.status$.next(status);

    return status;
  }

  private fehlertext(fehler: any): string {
    if (fehler?.status === 403) {
      return 'Für diese Inbetriebnahme fehlt die Berechtigung.';
    }

    if (fehler?.status === 404) {
      return 'Die Inbetriebnahme wurde nicht gefunden.';
    }

    if (fehler?.status === 401) {
      return 'Die Anmeldung ist abgelaufen. Bitte neu anmelden und es erneut versuchen.';
    }

    return (
      fehler?.error?.message
      ?? fehler?.message
      ?? 'Die Offline-Daten konnten nicht geladen werden. Besteht eine Verbindung?'
    );
  }
}
